import type { OverlayTriggerProps, OverlayTriggerState } from "@react-stately/overlays";
import { type MutableRefObject, type RefObject, useEffect, useRef } from "react";
import { mergeProps, useModal, useOverlay, useOverlayTrigger } from "react-aria";
import { useOverlayTriggerState } from "react-stately";

import type { OverlayOptions } from "../types/overlay.d.ts";
import { useAnimeState } from "./useAnimeState.ts";
import { useOverlayWithPosition } from "./useOverlayWithPosition.ts";

/** @deprecated use Modal or PopOver with useOverlay */
export const useDialog = <D extends HTMLElement>(
  options: Omit<OverlayOptions, "placement"> & OverlayTriggerProps = {},
  dialogRef?: MutableRefObject<D> | RefObject<D> | null,
) => {
  let innerDialogRef = useRef<D>(null);
  innerDialogRef = dialogRef ?? innerDialogRef;

  const state = useOverlayTriggerState(options as OverlayTriggerProps);
  const { overlayProps, underlayProps } = useOverlay(
    {
      onClose: () => {
        state.close();
      },
      isOpen: state.isOpen,
      shouldCloseOnBlur: options?.shouldCloseOnBlur,
      isKeyboardDismissDisabled: options?.isKeyboardDismissDisabled ?? false,
      isDismissable: options?.isDismissable ?? false,
    },
    innerDialogRef,
  );
  const { modalProps } = useModal({ isDisabled: options?.isDisabled });
  const animeState = useAnimeState(innerDialogRef, state, "top");

  return {
    dialogRef: innerDialogRef,
    dialogProps: mergeProps(overlayProps, modalProps),
    underlayProps,
    state: animeState as OverlayTriggerState,
  };
};

/**
 * Animation and Offset are not configurable to keep think simple
 * @deprecated use Modal or PopOver with useOverlay
 */
export const useDialogWithPosition = <T extends HTMLElement | SVGElement, D extends HTMLElement>(
  options: OverlayOptions & OverlayTriggerProps = {},
  triggerRef?: MutableRefObject<T> | null,
  dialogRef?: MutableRefObject<D> | null,
) => {
  let innerTriggerRef = useRef<T>(null);
  let innerDialogRef = useRef<D>(null);
  innerTriggerRef = triggerRef ?? innerTriggerRef;
  innerDialogRef = dialogRef ?? innerDialogRef;

  const state = useOverlayTriggerState(options as OverlayTriggerProps);
  const { triggerProps, overlayProps: overlayTriggerProps } = useOverlayTrigger(
    { type: "dialog" },
    state,
    innerTriggerRef,
  );
  const { overlayProps, arrowProps, placement, updatePosition } = useOverlayWithPosition(
    innerTriggerRef,
    innerDialogRef,
    state,
    {
      placement: "bottom",
      offset: 5,
      shouldCloseOnBlur: false,
      ...options,
    },
  );
  const { modalProps } = useModal({ isDisabled: options?.isDisabled });
  const animeState = useAnimeState(innerDialogRef, state, placement!);

  // Close the Popover (Dialog) on window resize
  useEffect(() => {
    const close = () => {
      state.close();
    };
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("resize", close);
    };
  }, [state]);

  return {
    triggerRef: innerTriggerRef,
    dialogRef: innerDialogRef,
    triggerProps,
    dialogProps: mergeProps(overlayTriggerProps, overlayProps, modalProps),
    arrowProps,
    state: animeState as OverlayTriggerState,
    updatePosition,
  };
};
